import { Component, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { MediaApiService } from "../../../services/media-controller/media-api.service";
import { MediaFile } from "../../../models/media-file";
import { MediaUsage } from "../../../models/media-usage";
import { Service } from "../../../models/service";
import { ServiceControllerService } from "../../../services/service-controller/service-controller.service";
import { Store } from "../../../services/store.service";

@Component({
  selector: "media-library",
  templateUrl: "./media-library.component.html",
  styles: [`
    .media-library-blocking__btn {
      display: inline-block;
      padding: 0.1rem 0.5rem;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.06);
      color: #1f2937;
      font-family: var(--bs-font-monospace, monospace);
      font-size: 0.875em;
      font-weight: 600;
      max-width: 18rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      vertical-align: middle;
    }
    .media-library-blocking__list li + li { margin-top: 2px; }
  `],
})
export class MediaLibraryComponent implements OnInit {

  public files: MediaFile[] = [];
  public loading = false;

  // When a delete is blocked by 409, we surface the referencing bots inline so the
  // admin can detach them before retrying. Cleared on next list refresh.
  public blockingUsages: { id: string; usages: MediaUsage[] } | null = null;

  // Single-element array whose value is bumped after each successful upload to
  // force the upload file-handler to remount in empty state, so consecutive
  // uploads don't show the previous file.
  public uploadSlot: number[] = [0];

  // serviceId -> service name lookup, populated once on init. Lets the blocking-
  // usages warning render a friendly service name instead of the raw UUID.
  public serviceNameById: { [id: string]: string } = {};

  constructor(
      private mediaApi: MediaApiService,
      private serviceController: ServiceControllerService,
      private toastr: ToastrService,
      public store: Store,
  ) {
  }

  public ngOnInit(): void {
    this.loadServices();
    this.refresh();
  }

  private loadServices(): void {
    this.serviceController.getServicesList().subscribe({
      next: (services: Service[]) => {
        const map: { [id: string]: string } = {};
        services.forEach((s) => {
          if (s?.id) {
            map[s.id] = s.name || s.id;
          }
        });
        this.serviceNameById = map;
      },
      error: () => { /* names are decorative; fall back to ids on failure */ },
    });
  }

  public serviceLabel(u: MediaUsage): string {
    return u.serviceName || this.serviceNameById[u.serviceId] || u.serviceId;
  }

  public usageLink(u: MediaUsage): any[] {
    if (u.context === "form" && u.formId) {
      return ["/services", u.serviceId, "forms", u.formId];
    }
    if (u.context === "role" && u.roleId) {
      return ["/services", u.serviceId, "roles", u.roleId];
    }
    if (u.context === "print-document" && u.documentId) {
      return ["/services", u.serviceId, "print-documents", u.documentId];
    }
    // No context from the backend yet. Bot-triggers route accepts ?key= and loads
    // the actions for that specific button — still closer than the service home.
    if (u.componentKey) {
      return ["/services", u.serviceId, "botstriggers"];
    }
    return ["/services", u.serviceId];
  }

  public usageQueryParams(u: MediaUsage): { [key: string]: string } | null {
    if (u.context) {
      return null;
    }
    if (u.componentKey) {
      return { key: u.componentKey };
    }
    return null;
  }

  public usageFragment(u: MediaUsage): string | null {
    return u.context === "role" && u.roleId ? "role-main-tab" : null;
  }

  public usageTargetLabel(u: MediaUsage): string {
    if (u.context === "form") {
      return u.formName || u.formId || this.serviceLabel(u);
    }
    if (u.context === "role") {
      return u.roleName || u.roleId || this.serviceLabel(u);
    }
    if (u.context === "print-document") {
      return u.documentName || u.documentId || this.serviceLabel(u);
    }
    return this.serviceLabel(u);
  }

  public usageTooltip(u: MediaUsage): string {
    switch (u.context) {
      case "form": return "Open form";
      case "role": return "Open role";
      case "print-document": return "Open print document";
      default: return u.componentKey ? "Open button actions" : "Open service";
    }
  }

  public refresh(): void {
    this.loading = true;
    this.blockingUsages = null;
    this.mediaApi.list().subscribe({
      next: (files) => {
        this.files = files;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error("Failed to load media library: " + (err?.message || "unknown error"));
      },
    });
  }

  public onUploaded(fileId: string | null): void {
    if (!fileId) {
      return;
    }
    this.toastr.success("File uploaded");
    this.uploadSlot = [this.uploadSlot[0] + 1];
    this.refresh();
  }

  public delete(file: MediaFile): void {
    this.mediaApi.delete(file.id).subscribe({
      next: () => {
        this.toastr.success("Deleted " + file.name);
        this.refresh();
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 409 && err.error?.usages) {
          // Invariant I2: the file is still referenced. Surface the list inline so the
          // admin can navigate to the bots and detach them.
          this.blockingUsages = { id: file.id, usages: err.error.usages };
          this.toastr.warning(
              "Detach the " + err.error.usages.length + " referencing bot(s) first",
              "In use",
          );
        } else {
          this.toastr.error("Delete failed: " + (err.message || "unknown error"));
        }
      },
    });
  }

  public trackById(_index: number, file: MediaFile): string {
    return file.id;
  }

  public trackBySlot(_index: number, slot: number): number {
    return slot;
  }
}
